import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, FileText, Mail, Phone, Smartphone, Crown, Search, Users, Award, Repeat, UserCog, ClipboardList, UserCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  manager: { label: "District Manager", cls: "bg-brand text-white border-brand" },
  dm_staff: { label: "DM Staff", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  recruiter: { label: "Recruiter", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const TABS = [
  { key: "owners", label: "Owners", icon: Crown },
  { key: "team", label: "Team", icon: UserCog },
  { key: "candidates", label: "Candidates", icon: ClipboardList },
];

export default function DistrictPhonebook() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("owners");
  const [data, setData] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [q, setQ] = useState("");
  const [awardFilter, setAwardFilter] = useState("all");
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    try {
      const [ow, ds] = await Promise.all([
        api.get(`/agency-districts/${orgId}/owners`),
        api.get("/agency-districts"),
      ]);
      setData(ow.data); setDistricts(ds.data.items);
    } catch (e) { toast.error(apiError(e)); navigate("/agencies"); }
  }, [orgId, navigate]);
  useEffect(() => { load(); setTab("owners"); }, [load]);
  if (!data) return <PageLoader />;

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/agency-districts/${orgId}/owners.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const safeName = data.district.name.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "district";
      const a = document.createElement("a");
      a.href = url; a.download = `${safeName}_owners.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setDownloading(""); }
  };

  const allAwards = Array.from(new Set(data.owners.flatMap((o) => o.awards || []))).sort();
  const owners = data.owners.filter((o) => {
    if (awardFilter !== "all" && !(o.awards || []).includes(awardFilter)) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [o.name, o.agency_name, o.email, o.mobile, o.phone, o.city, o.agent_number].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const multiDistrict = districts.length > 1;

  return (
    <div>
      <button onClick={() => navigate("/agencies")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-3" data-testid="back-to-districts"><ArrowLeft className="w-4 h-4" /> Districts</button>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="district-title">{data.district.name}</h1>
            {multiDistrict && (
              <div className="flex items-center gap-1.5 rounded-full bg-brand/10 pl-3 pr-1 py-1" data-testid="district-switcher">
                <Repeat className="w-3.5 h-3.5 text-brand" />
                <span className="text-xs text-slate-500">Viewing:</span>
                <Select value={orgId} onValueChange={(v) => v !== orgId && navigate(`/agencies/districts/${v}`)}>
                  <SelectTrigger className="h-7 border-0 bg-transparent text-xs font-semibold text-brand focus:ring-0 px-1" data-testid="district-switch-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{districts.map((d) => <SelectItem key={d.org_id} value={d.org_id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-0.5 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {data.owners.length} agency owners · District {data.district.org_code}</p>
        </div>
        {tab === "owners" && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => download("csv")} disabled={!!downloading} data-testid="download-csv"><Download className="w-4 h-4 mr-1.5" /> {downloading === "csv" ? "…" : "CSV"}</Button>
            <Button variant="outline" onClick={() => download("pdf")} disabled={!!downloading} data-testid="download-pdf"><FileText className="w-4 h-4 mr-1.5" /> {downloading === "pdf" ? "…" : "PDF"}</Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto tr-scroll [&>*]:shrink-0" data-testid="district-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "owners" && (
        <OwnersTab owners={owners} allOwners={data.owners} allAwards={allAwards} awardSummary={data.award_summary}
          q={q} setQ={setQ} awardFilter={awardFilter} setAwardFilter={setAwardFilter} navigate={navigate} />
      )}
      {tab === "team" && <TeamTab orgId={orgId} />}
      {tab === "candidates" && <CandidatesTab orgId={orgId} navigate={navigate} />}
    </div>
  );
}

function OwnersTab({ owners, allOwners, allAwards, awardSummary, q, setQ, awardFilter, setAwardFilter, navigate }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search owners, agency, phone…" className="pl-9" data-testid="phonebook-search" />
        </div>
        {allAwards.length > 0 && (
          <Select value={awardFilter} onValueChange={setAwardFilter}>
            <SelectTrigger className="h-10 w-52 text-sm" data-testid="award-filter"><Award className="w-4 h-4 mr-1 text-amber-500" /><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All awards</SelectItem>
              {allAwards.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {awardSummary && awardSummary.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="award-summary-bar">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Award className="w-3.5 h-3.5 text-amber-500" /> Awards:</span>
          {awardSummary.map((a) => (
            <button key={a.award} onClick={() => setAwardFilter(awardFilter === a.award ? "all" : a.award)}
              data-testid={`award-chip-${a.award.replace(/\s+/g, '-')}`}
              className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${awardFilter === a.award ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}>
              {a.award} <span className="font-bold">{a.count}</span>
            </button>
          ))}
          {awardFilter !== "all" && <button onClick={() => setAwardFilter("all")} className="text-xs text-slate-400 hover:text-brand underline">clear</button>}
        </div>
      )}
      {owners.length ? (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100" data-testid="phonebook-list">
          {owners.map((o) => (
            <div key={o.person_id} data-testid={`phonebook-row-${o.person_id}`}
              className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-3 hover:bg-slate-50">
              <button onClick={() => navigate(`/people/detail/${o.person_id}`)} className="min-w-[210px] text-left" data-testid={`owner-link-${o.person_id}`}>
                <span className="font-semibold text-slate-800 hover:text-brand flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-amber-500" />{o.name}
                  {o.preferred_name && <span className="text-xs text-slate-400 font-normal">"{o.preferred_name}"</span>}
                </span>
                <span className="text-xs text-slate-400">{o.agency_name || "—"}{o.agent_number ? ` · ${o.agent_number}` : ""}</span>
              </button>
              <a href={o.email ? `mailto:${o.email}` : undefined} className={`text-sm flex items-center gap-1.5 ${o.email ? "text-slate-600 hover:text-brand" : "text-slate-300"}`}><Mail className="w-3.5 h-3.5" /> {o.email || "—"}</a>
              <span className="text-sm text-slate-600 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-slate-400" /> {o.mobile || "—"}</span>
              <span className="text-sm text-slate-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" /> {o.phone || "—"}</span>
              <span className="text-sm text-slate-500">{[o.city, o.state].filter(Boolean).join(", ") || "—"}</span>
              {(o.awards || []).length > 0 && (
                <span className="flex flex-wrap gap-1" data-testid={`awards-${o.person_id}`}>
                  {o.awards.map((a) => <span key={a} className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 flex items-center gap-0.5"><Award className="w-2.5 h-2.5" />{a}</span>)}
                </span>
              )}
              <span className="text-xs text-slate-400 ml-auto flex items-center gap-1"><Users className="w-3 h-3" /> {o.staff_count} staff</span>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={Users} title="No agency owners" description="No owners match your filters in this district." />}
    </>
  );
}

function TeamTab({ orgId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/agency-districts/${orgId}/managers`).then((r) => setItems(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, [orgId]);
  if (!items) return <PageLoader />;
  if (!items.length) return <EmptyState icon={UserCog} title="No team members" description="No District Managers, DM Staff, or Recruiters are assigned to this district yet." />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100" data-testid="team-list">
      {items.map((m) => {
        const meta = ROLE_META[m.role] || { label: m.role, cls: "bg-slate-100 text-slate-600 border-slate-200" };
        return (
          <div key={m.user_id} data-testid={`team-row-${m.user_id}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-3 hover:bg-slate-50">
            <span className="min-w-[200px] font-semibold text-slate-800 flex items-center gap-1.5"><UserCog className="w-3.5 h-3.5 text-slate-400" />{m.name || "—"}</span>
            <a href={m.email ? `mailto:${m.email}` : undefined} className={`text-sm flex items-center gap-1.5 ${m.email ? "text-slate-600 hover:text-brand" : "text-slate-300"}`}><Mail className="w-3.5 h-3.5" /> {m.email || "—"}</a>
            <span className={`text-xs font-semibold rounded-full px-2.5 py-1 border ml-auto ${meta.cls}`} data-testid={`team-role-${m.user_id}`}>{meta.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function CandidatesTab({ orgId, navigate }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [recruiter, setRecruiter] = useState("");
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/agency-districts/${orgId}/candidates`); setData(r.data); setSelected(new Set()); }
    catch (e) { toast.error(apiError(e)); setData({ candidates: [], recruiters: [], summary: {}, can_manage: false }); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageLoader />;

  const cands = data.candidates || [];
  const toggle = (pid) => setSelected((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  const allSelected = cands.length > 0 && selected.size === cands.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(cands.map((c) => c.person_id)));

  const bulkAssign = async () => {
    if (!selected.size) return;
    setAssigning(true);
    try {
      const { data: res } = await api.post(`/agency-districts/${orgId}/bulk-assign-recruiter`,
        { recruiter_user_id: recruiter === "none" || !recruiter ? null : recruiter, person_ids: Array.from(selected) });
      toast.success(`${res.updated} candidate(s) ${res.assigned_recruiter_name ? `assigned to ${res.assigned_recruiter_name}` : "unassigned"}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setAssigning(false); }
  };

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 mb-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="summary-by-status">
          <p className="text-xs font-semibold text-slate-500 mb-2">Pipeline by status · {data.summary?.total || 0} candidates</p>
          <div className="flex flex-wrap gap-2">
            {(data.summary?.by_status || []).length ? data.summary.by_status.map((s) => (
              <span key={s.status} className="text-xs rounded-full px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200">{s.label} <span className="font-bold">{s.count}</span></span>
            )) : <span className="text-xs text-slate-400">No candidates</span>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="summary-by-recruiter">
          <p className="text-xs font-semibold text-slate-500 mb-2">Candidates per recruiter</p>
          <div className="flex flex-wrap gap-2">
            {(data.summary?.by_recruiter || []).length ? data.summary.by_recruiter.map((r) => (
              <span key={r.recruiter_id || "none"} className={`text-xs rounded-full px-2.5 py-1 border flex items-center gap-1 ${r.recruiter_id ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-600 border-rose-200"}`}><UserCheck className="w-3 h-3" />{r.name} <span className="font-bold">{r.count}</span></span>
            )) : <span className="text-xs text-slate-400">—</span>}
          </div>
        </div>
      </div>

      {data.can_manage && (
        <div className="flex flex-wrap items-center gap-3 mb-4 rounded-lg border border-brand/20 bg-brand/5 px-4 py-3" data-testid="bulk-assign-bar">
          <span className="text-sm font-semibold text-slate-700">{selected.size} selected</span>
          <Select value={recruiter} onValueChange={setRecruiter}>
            <SelectTrigger className="h-9 w-64 text-sm bg-white" data-testid="bulk-recruiter-select"><SelectValue placeholder="Choose recruiter…" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="none">Unassign</SelectItem>
              {(data.recruiters || []).map((r) => <SelectItem key={r.user_id} value={r.user_id}>{r.name} · {r.role}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="bg-brand hover:bg-brand-strong" disabled={!selected.size || !recruiter || assigning} onClick={bulkAssign} data-testid="bulk-assign-btn">
            {assigning ? "Assigning…" : "Assign recruiter"}
          </Button>
        </div>
      )}

      {cands.length ? (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="candidates-list">
          <div className="flex items-center gap-3 px-5 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500">
            {data.can_manage && <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-candidates" />}
            <span className="flex-1">Candidate</span>
            <span className="w-40 hidden md:block">Type</span>
            <span className="w-28 hidden md:block">Status</span>
            <span className="w-48">Assigned Recruiter</span>
          </div>
          {cands.map((c) => (
            <div key={c.person_id} data-testid={`candidate-row-${c.person_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0">
              {data.can_manage && <Checkbox checked={selected.has(c.person_id)} onCheckedChange={() => toggle(c.person_id)} data-testid={`select-candidate-${c.person_id}`} />}
              <button onClick={() => navigate(`/people/detail/${c.person_id}`)} className="flex-1 text-left">
                <span className="font-medium text-slate-800 hover:text-brand">{c.name}{c.preferred_name && <span className="text-xs text-slate-400 font-normal"> "{c.preferred_name}"</span>}</span>
                <span className="block text-xs text-slate-400 font-mono">{c.human_id}{c.opportunity_count > 1 ? ` · ${c.opportunity_count} opps` : ""}</span>
              </button>
              <span className="w-40 hidden md:block text-sm text-slate-600">{c.candidate_type_label || "—"}</span>
              <span className="w-28 hidden md:block text-xs"><span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{c.status_label || "—"}</span></span>
              <span className={`w-48 text-sm ${c.assigned_recruiter_name ? "text-slate-700" : "text-rose-500"}`} data-testid={`candidate-recruiter-${c.person_id}`}>{c.assigned_recruiter_name || "Unassigned"}</span>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={ClipboardList} title="No candidates" description="No candidates (recruiting opportunities) exist in this district yet." />}
    </>
  );
}
